import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { CreditCard, Plus, Search, Link2, Wallet, CheckCircle2, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreditsPanel() {
  const [credits, setCredits] = useState([]);
  const [openTotal, setOpenTotal] = useState(0);
  const [filter, setFilter] = useState('open');
  const [showAdd, setShowAdd] = useState(false);
  const [newCredit, setNewCredit] = useState({ customer_name: '', contact: '', opening_amount: '', notes: '' });

  // Payment dialog
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('cash');
  const [payNote, setPayNote] = useState('');

  // Link-sale dialog
  const [linkTarget, setLinkTarget] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchCredits = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/credits${filter ? `?status=${filter}` : ''}`, { headers });
      setCredits(res.data.credits || []);
      setOpenTotal(res.data.open_total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load credits');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const saveNewCredit = async () => {
    if (!newCredit.customer_name.trim()) { toast.error('Customer name required'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/credits`, {
        ...newCredit,
        opening_amount: parseFloat(newCredit.opening_amount || 0),
      }, { headers });
      toast.success('Credit created');
      setShowAdd(false);
      setNewCredit({ customer_name: '', contact: '', opening_amount: '', notes: '' });
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const savePayment = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) { toast.error('Enter amount'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/credits/${payTarget.id}/payments`, {
        amount: parseFloat(payAmount),
        method: payMethod,
        note: payNote,
      }, { headers });
      toast.success('Payment recorded');
      setPayTarget(null); setPayAmount(''); setPayNote(''); setPayMethod('cash');
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    }
  };

  const runSaleSearch = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/sales/search?q=${encodeURIComponent(searchQ || '')}&limit=25`, { headers });
      setSearchResults(res.data || []);
    } catch (e) {
      toast.error('Search failed');
    }
  };

  const linkSale = async (sale) => {
    try {
      await axios.post(`${API_URL}/api/auto-spares/credits/${linkTarget.id}/link-sale`, { sale_id: sale.id }, { headers });
      toast.success(`Sale ${sale.id.slice(0, 8)} linked (${R(sale.total)})`);
      // remove from local list so double-clicks don't re-attach
      setSearchResults(prev => prev.filter(s => s.id !== sale.id));
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    }
  };

  const deleteCredit = async (c) => {
    if (!window.confirm(`Delete credit for ${c.customer_name}? Linked sales stay in the sales log; the credit record is removed.`)) return;
    try {
      await axios.delete(`${API_URL}/api/auto-spares/credits/${c.id}`, { headers });
      toast.success('Credit deleted');
      fetchCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white flex items-center gap-2"><CreditCard className="h-5 w-5 text-rose-400" /> Customer Credits</CardTitle>
          <p className="text-xs text-slate-400 mt-1">Credit sales earn <b>no commission</b>. Salesman only earns once the debt is settled off-line.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-900 text-white text-xs border border-slate-600 rounded px-2 py-1"
            data-testid="credits-filter"
          >
            <option value="open">Open</option>
            <option value="settled">Settled</option>
            <option value="">All</option>
          </select>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAdd(true)} data-testid="credit-add-btn">
            <Plus className="h-4 w-4 mr-1" /> New Credit
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-rose-900/40 border border-rose-700 rounded-lg p-3">
            <div className="text-xs text-rose-300 uppercase font-semibold">Total Open</div>
            <div className="text-2xl font-bold text-white" data-testid="credits-open-total">{R(openTotal)}</div>
          </div>
          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
            <div className="text-xs text-slate-400 uppercase font-semibold">Records ({filter || 'all'})</div>
            <div className="text-2xl font-bold text-white">{credits.length}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Contact</TableHead>
                <TableHead className="text-slate-400 text-right">Opening</TableHead>
                <TableHead className="text-slate-400 text-right">Balance</TableHead>
                <TableHead className="text-slate-400 text-center">Sales</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {credits.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No credits.</TableCell></TableRow>
              ) : credits.map(c => (
                <TableRow key={c.id} className="border-slate-700/50" data-testid={`credit-row-${c.id}`}>
                  <TableCell className="text-white font-semibold">{c.customer_name}</TableCell>
                  <TableCell className="text-slate-400 text-xs">{c.contact || '—'}</TableCell>
                  <TableCell className="text-right text-slate-300 font-mono">{R(c.opening_amount)}</TableCell>
                  <TableCell className={`text-right font-mono font-bold ${c.balance > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{R(c.balance)}</TableCell>
                  <TableCell className="text-center text-slate-400 text-xs">{(c.sale_ids || []).length}</TableCell>
                  <TableCell>
                    <Badge className={c.status === 'settled' ? 'bg-emerald-600' : 'bg-rose-600'}>
                      {c.status === 'settled' ? <><CheckCircle2 className="h-3 w-3 mr-1" />Settled</> : 'Open'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {c.status === 'open' && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1" onClick={() => setPayTarget(c)} data-testid={`credit-pay-${c.id}`}>
                          <Wallet className="h-3.5 w-3.5 mr-1" /> Pay
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setLinkTarget(c); setSearchQ(''); setSearchResults([]); }} data-testid={`credit-link-${c.id}`}>
                          <Link2 className="h-3.5 w-3.5 mr-1" /> Link Sale
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="ml-1" onClick={() => deleteCredit(c)} data-testid={`credit-delete-${c.id}`} title="Delete credit">
                      <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* New Credit Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Credit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Customer *</Label><Input value={newCredit.customer_name} onChange={(e) => setNewCredit({ ...newCredit, customer_name: e.target.value })} data-testid="credit-add-customer" /></div>
            <div><Label>Contact</Label><Input value={newCredit.contact} onChange={(e) => setNewCredit({ ...newCredit, contact: e.target.value })} /></div>
            <div><Label>Opening Amount</Label><Input type="number" step="0.01" value={newCredit.opening_amount} onChange={(e) => setNewCredit({ ...newCredit, opening_amount: e.target.value })} data-testid="credit-add-amount" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={newCredit.notes} onChange={(e) => setNewCredit({ ...newCredit, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveNewCredit} data-testid="credit-add-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Record Payment — {payTarget?.customer_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Outstanding balance: <b>{R(payTarget?.balance)}</b></p>
          <div className="space-y-3 pt-2">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="credit-pay-amount" autoFocus /></div>
            <div>
              <Label>Method</Label>
              <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="w-full border rounded px-2 py-2" data-testid="credit-pay-method">
                <option value="cash">Cash</option>
                <option value="eft">EFT</option>
                <option value="online">Online</option>
                <option value="card">Card</option>
              </select>
            </div>
            <div><Label>Note</Label><Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="e.g. deposit slip #123" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={savePayment} data-testid="credit-pay-save">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Sale Dialog */}
      <Dialog open={!!linkTarget} onOpenChange={(o) => !o && setLinkTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Link Sale to Credit — {linkTarget?.customer_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Search past sales by customer name, part description, reference or sale ID. Linking a sale flips its payment method to <b>Credit</b> and removes commission.</p>
          <div className="flex gap-2 pt-2">
            <Input placeholder="Search sales…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSaleSearch()} data-testid="sale-search-input" />
            <Button onClick={runSaleSearch} className="bg-blue-600 hover:bg-blue-700" data-testid="sale-search-btn"><Search className="h-4 w-4 mr-1" /> Search</Button>
          </div>
          <div className="max-h-96 overflow-y-auto mt-3 border rounded">
            {searchResults.length === 0 ? (
              <p className="text-center text-slate-500 py-8 text-sm">Enter a search term above and press Search.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>PM</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map(s => (
                    <TableRow key={s.id} data-testid={`sale-search-row-${s.id}`}>
                      <TableCell className="text-xs">{s.date}</TableCell>
                      <TableCell className="text-xs">{s.customer_name || '—'}</TableCell>
                      <TableCell className="text-xs max-w-[220px] truncate" title={s.part_description}>{s.part_description}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{R(s.total)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] capitalize">{s.payment_method}</Badge></TableCell>
                      <TableCell className="text-right">
                        {s.linked_credit_id ? (
                          <span className="text-xs text-slate-500">already linked</span>
                        ) : (
                          <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => linkSale(s)} data-testid={`link-sale-${s.id}`}>
                            <Link2 className="h-3.5 w-3.5 mr-1" /> Link
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkTarget(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
